import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { useAlert } from "react-alert";
import {
  ErrorOutline,
  Refresh,
  SchoolOutlined,
  ShowChartOutlined,
} from "@material-ui/icons";
import { useParent } from "../../../../context/ParentContext";
import { baseUrl } from "../../../../services/config";
import "../MonthlyStudentAttendance/parent-progress.css";
import "./progression-charts.css";

const getStudentId = (child) =>
  child?.student?.id || child?.student_id || child?.id || null;
const getClassId = (child) =>
  child?.student_class_id ||
  child?.student?.student_class_id ||
  child?.student?.student_class?.id ||
  null;

function toNumber(value) {
  const number = Number(value || 0);
  return Number.isNaN(number) ? 0 : number;
}

export default function ProgressionCharts() {
  const alert = useAlert();
  const { selectedChild } = useParent();
  const [sessions, setSessions] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [yearId, setYearId] = useState("");
  const [classId, setClassId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [report, setReport] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [error, setError] = useState("");
  const studentId = getStudentId(selectedChild);
  const currentClassId = getClassId(selectedChild);
  const studentName = selectedChild?.student?.name || "Student";

  const headers = (json = false) => {
    const token = localStorage.getItem("parentToken");
    return {
      Accept: "application/json",
      ...(json ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const loadFilters = async () => {
    if (!studentId) return;
    setFiltersLoading(true);
    setError("");
    setSessions([]);
    setSubjects([]);
    setClasses([]);
    setReport([]);
    try {
      const response = await fetch(
        `${baseUrl}/parent-student-sessions-list/${studentId}`,
        { headers: headers() },
      );
      const result = await response.json();
      if (!response.ok)
        throw new Error(result?.message || "Unable to load report filters.");
      const payload = result?.payload || {};
      const sessionList = Array.isArray(payload.student_exam_sessions_list)
        ? payload.student_exam_sessions_list
        : [];
      const subjectList = Array.isArray(payload.student_exam_subjects_list)
        ? payload.student_exam_subjects_list
        : [];
      const classList = Array.isArray(payload.student_exam_classes_list)
        ? payload.student_exam_classes_list
        : [];
      if (!sessionList.length || !classList.length) {
        const message = "No academic progression history found for this student.";
        setError(message);
        alert.show(message);
        return;
      }
      setSessions(sessionList);
      setSubjects(subjectList);
      setClasses(classList);
      const activeSession = sessionList.find(
        (session) => String(session.active_academic_year) === "1",
      );
      const matchingClass = classList.find(
        (studentClass) => String(studentClass.id) === String(currentClassId),
      );
      setYearId(String(activeSession?.id || sessionList[sessionList.length - 1]?.id || ""));
      setClassId(String(matchingClass?.id || classList[classList.length - 1]?.id || ""));
      setSubjectId("");
    } catch (requestError) {
      const message = requestError.message || "Unable to load report filters.";
      setError(message);
      alert.error(message);
    } finally {
      setFiltersLoading(false);
    }
  };

  const loadReport = async () => {
    if (!studentId || !yearId || !classId) return;
    setReportLoading(true);
    setError("");
    setReport([]);
    try {
      const response = await fetch(`${baseUrl}/parent-student-exam-report`, {
        method: "POST",
        headers: headers(true),
        body: JSON.stringify({
          year_id: Number(yearId),
          student_class_id: Number(classId),
          student_id: Number(studentId),
          subject_id: subjectId ? Number(subjectId) : null,
        }),
      });
      const result = await response.json();
      if (!response.ok)
        throw new Error(result?.message || "Unable to load progression report.");
      const payload = result?.payload || result;
      if (!Array.isArray(payload) || !payload.length) {
        const message = "No exam results found for the selected filters.";
        setError(message);
        alert.show(message);
        return;
      }
      setReport(payload);
    } catch (requestError) {
      const message = requestError.message || "Unable to load progression report.";
      setError(message);
      alert.error(message);
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, yearId, classId, subjectId]);

  const stats = useMemo(() => {
    const percentages = report.map((item) => toNumber(item.result?.percentage));
    const average = percentages.length
      ? percentages.reduce((sum, value) => sum + value, 0) / percentages.length
      : 0;
    const highest = percentages.length ? Math.max(...percentages) : 0;
    const latest = percentages.length ? percentages[percentages.length - 1] : 0;
    const first = percentages.length ? percentages[0] : 0;
    return { average, highest, latest, change: latest - first };
  }, [report]);

  const chartOptions = useMemo(
    () => ({
      chart: { toolbar: { show: false }, zoom: { enabled: false }, fontFamily: "inherit" },
      colors: ["#7041cb"],
      dataLabels: { enabled: true, formatter: (value) => `${Number(value).toFixed(1)}%`, offsetY: -8, style: { fontSize: "11px", colors: ["#4b5563"] }, background: { enabled: false } },
      stroke: { curve: "smooth", width: 3 },
      markers: { size: 5, strokeWidth: 3, strokeColors: "#fff", hover: { size: 7 } },
      fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: .32, opacityTo: .04, stops: [0, 90, 100] } },
      grid: { borderColor: "#e9edf3", strokeDashArray: 4, padding: { top: 20, right: 15, left: 10 } },
      xaxis: { categories: report.map((item) => item.exam || "Exam"), labels: { style: { colors: "#6f7c8f", fontSize: "11px" }, trim: true }, axisBorder: { show: false }, axisTicks: { show: false } },
      yaxis: { min: 0, max: 100, tickAmount: 5, labels: { formatter: (value) => `${value}%`, style: { colors: "#8490a2", fontSize: "11px" } } },
      tooltip: { y: { formatter: (value) => `${Number(value).toFixed(2)}%` } },
    }),
    [report],
  );
  const chartSeries = useMemo(
    () => [{ name: "Percentage", data: report.map((item) => toNumber(item.result?.percentage)) }],
    [report],
  );

  const selectedSubject = subjects.find(
    (subject) => String(subject.id) === String(subjectId),
  );

  return (
    <section className="parent-progress-page parent-progression-page">
      <div className="parent-progress-hero parent-progression-hero">
        <div><span className="parent-progress-eyebrow">Performance analytics</span><h1>Progression Charts</h1><p>{selectedChild ? `Track exam-to-exam academic performance for ${studentName}.` : "Select a child to view academic progression."}</p></div>
        <div className="parent-progression-filters">
          <Filter label="Session" value={yearId} onChange={setYearId} loading={filtersLoading} options={sessions.map((item) => ({ id: item.id, label: item.year }))} />
          <Filter label="Class" value={classId} onChange={setClassId} loading={filtersLoading} options={classes.map((item) => ({ id: item.id, label: item.name }))} />
          <Filter label="Subject" value={subjectId} onChange={setSubjectId} loading={filtersLoading} placeholder="All subjects" options={subjects.map((item) => ({ id: item.id, label: item.name }))} allowEmpty />
        </div>
      </div>

      {!studentId ? (
        <PageState icon={SchoolOutlined} title="No student selected" text="Please select a child from the top menu to view progression." />
      ) : filtersLoading || reportLoading ? (
        <PageState loading title="Loading progression" text="Preparing academic performance data." />
      ) : error ? (
        <PageState icon={ErrorOutline} error title="Progression unavailable" text={error} onRetry={sessions.length ? loadReport : loadFilters} />
      ) : report.length ? (
        <>
          <div className="parent-progression-summary"><Summary label="Latest result" value={`${stats.latest.toFixed(1)}%`} tone="violet" /><Summary label="Average" value={`${stats.average.toFixed(1)}%`} tone="blue" /><Summary label="Highest result" value={`${stats.highest.toFixed(1)}%`} tone="green" /><Summary label="Overall change" value={`${stats.change >= 0 ? "+" : ""}${stats.change.toFixed(1)}%`} tone={stats.change >= 0 ? "green" : "red"} /></div>
          <div className="parent-progression-panel"><div className="parent-progression-panel-head"><div><h2>{selectedSubject?.name || "Overall performance"}</h2><p>Percentage achieved across {report.length} exam{report.length === 1 ? "" : "s"}</p></div><ShowChartOutlined /></div><div className="parent-progression-chart"><Chart options={chartOptions} series={chartSeries} type="area" height={340} /></div></div>
          <ReportTable report={report} />
        </>
      ) : (
        <PageState icon={ShowChartOutlined} title="No progression data" text="Select a session, class and subject to view performance." />
      )}
    </section>
  );
}

function Filter({ label, value, onChange, loading, options, placeholder, allowEmpty }) {
  return <label><span>{label}</span><select value={value} onChange={(event) => onChange(event.target.value)} disabled={loading || !options.length}>{(allowEmpty || !value) && <option value="">{loading ? "Loading..." : placeholder || `Select ${label.toLowerCase()}`}</option>}{options.map((option) => <option value={option.id} key={option.id}>{option.label}</option>)}</select></label>;
}

function Summary({ label, value, tone }) {
  return <article className={`parent-progression-stat ${tone}`}><span>{label}</span><strong>{value}</strong></article>;
}

function ReportTable({ report }) {
  return <div className="parent-progression-table-panel"><div className="parent-progression-table-title"><h2>Exam breakdown</h2><span>{report.length} results</span></div><div className="parent-progression-table-wrap"><table><thead><tr><th>Exam</th><th>Obtained marks</th><th>Total marks</th><th>Percentage</th></tr></thead><tbody>{report.map((item, index) => <tr key={`${item.exam}-${index}`}><td data-label="Exam"><strong>{item.exam || `Exam ${index + 1}`}</strong></td><td data-label="Obtained marks">{toNumber(item.result?.gain_marks)}</td><td data-label="Total marks">{toNumber(item.result?.total_marks)}</td><td data-label="Percentage"><span className={toNumber(item.result?.percentage) >= 40 ? "positive" : "negative"}>{toNumber(item.result?.percentage).toFixed(2)}%</span></td></tr>)}</tbody></table></div></div>;
}

function PageState({ icon: Icon, title, text, loading, error, onRetry }) {
  return <div className={`parent-progression-state${error ? " error" : ""}`} role={error ? "alert" : "status"}>{loading ? <span className="parent-progression-spinner" /> : Icon && <Icon />}<h2>{title}</h2><p>{text}</p>{onRetry && <button type="button" onClick={onRetry}><Refresh /> Try again</button>}</div>;
}
